// Resolve a member's saved pedigree-config node.
//
// The config array (pedigreeconfigs.pedigreeConfig) stores one node per person
// carrying the structural links (fatherId/motherId/spouseIds/childrenIds, by
// pid) plus layout state. Historically a node's `uid` was the client-minted
// id: a UUID for pre-born-stable members, rewritten to the Mongo _id on save.
// So a member and its config node can legitimately agree on ANY of
// _id / uid / pid, depending on when the family was last saved:
//   • born-stable / re-saved families: node.uid === member._id
//   • legacy UUID families:            node.uid === member.uid (a UUID) !== member._id
//   • the root/proband node:           node.uid === root._id === user._id
//
// Keying OR looking up by a single id therefore silently drops relationships
// for whichever cohort doesn't match. That was the TGC-1429 regression: the
// config map was keyed by node.uid while the merge looked up by member._id, so
// every legacy (UUID) member resolved to {} and lost its links — only the root
// (whose node.uid === user._id) stayed connected.
//
// buildConfigMap indexes each node under every id it carries; resolveConf tries
// a member's ids most-stable-first. pid is the last resort because the
// proband-decoupling feature can reassign pids.

const KEY_ORDER = ['uid', '_id', 'pid'];

export function buildConfigMap(configArray) {
  const map = new Map();
  if (!Array.isArray(configArray)) return map;
  for (const conf of configArray) {
    if (!conf) continue;
    for (const field of KEY_ORDER) {
      const key = conf[field];
      if (key === undefined || key === null) continue;
      const k = String(key);
      // First writer wins: uid is the canonical identity, so a later node's
      // pid can never shadow an earlier node's uid/_id entry.
      if (!map.has(k)) map.set(k, conf);
    }
  }
  return map;
}

export function resolveConf(map, member) {
  if (!map || typeof map.get !== 'function' || !member) return {};
  for (const field of ['_id', 'uid', 'pid']) {
    const key = member[field];
    if (key === undefined || key === null) continue;
    const conf = map.get(String(key));
    if (conf) return conf;
  }
  return {};
}
